import { useMemo } from 'react';
import { DataGrid } from '@mui/x-data-grid';
import { getApp } from 'firebase/app';
import { getStorage } from 'firebase/storage';
import { useNavigate } from 'react-router-dom';
import Box from '@mui/material/Box';
import TextField from '@mui/material/TextField';
import InputAdornment from '@mui/material/InputAdornment';
import Alert from '@mui/material/Alert';
import Typography from '@mui/material/Typography';
import SearchIcon from '@mui/icons-material/Search';
import { useAuth } from '../../shared/context/AuthContext';
import { formatDateDDMMYYYY } from '../../shared/utils/dateUtils';
import UploadButton from './UploadButton.jsx';
import PatientProgressCell from './PatientProgressCell.jsx';
import OfflineStatusCell from './OfflineStatusCell.jsx';
import OfflineActionsCell from './OfflineActionsCell.jsx';
import { useStaleUploadCleanup } from '../hooks/useStaleUploadCleanup.js';

const dicomBucketName = `${import.meta.env.VITE_STORAGE_DICOM_PREFIX}.${import.meta.env.VITE_LOCATION_SHORTHAND}.${import.meta.env.VITE_ENVIRONMENT}.replico.tech`
const SEARCH_ERROR_ALERT_ID = 'patients-search-error-alert';

/**
 * Patients data grid component - server-paginated with server-side name search.
 * @param {{
 *   rows?: any[],
 *   loading?: boolean,
 *   error?: boolean,
 *   defaultPageSize?: number,
 *   pageSizeOptions?: number[],
 *   isDemo?: boolean,
 *   isDemoOffline?: boolean,
 *   paginationMode?: 'server' | 'client',
 *   rowCount?: number,
 *   paginationModel?: { page: number, pageSize: number },
 *   onPaginationModelChange?: (model: { page: number, pageSize: number }) => void,
 *   searchTerm?: string,
 *   onSearchTermChange?: (term: string) => void,
 *   isSearching?: boolean,
 *   searchTruncated?: boolean,
 * }} props
 */
export default function PatientsDataGrid(props) {
  const {
    rows = [],
    loading = false,
    error = false,
    pageSizeOptions = [10, 25, 50],
    isDemo = false,
    isDemoOffline = false,
    paginationMode = 'client',
    rowCount,
    paginationModel,
    onPaginationModelChange,
    searchTerm = '',
    onSearchTermChange,
    isSearching = false,
    searchTruncated = false,
  } = props;
  const navigate = useNavigate();
  const { auth } = useAuth();
  const app = getApp();
  const storage = getStorage(app, dicomBucketName);

  useStaleUploadCleanup(auth, isDemo);

  const handleSearchChange = (event) => {
    if (onSearchTermChange) onSearchTermChange(event.target.value);
  };

  /** @type {import('@mui/x-data-grid').GridColDef[]} */
  const columns = useMemo(() => [
    {
      field: 'name',
      headerName: 'Name',
      flex: 3,
      minWidth: 150,
      sortable: false,
      filterable: false,
      disableColumnMenu: true,
    },
    {
      field: 'examDate',
      headerName: 'Exam Date',
      flex: 1,
      minWidth: 130,
      sortable: false,
      filterable: false,
      disableColumnMenu: true,
      valueFormatter: (value) => formatDateDDMMYYYY(value),
    },
    {
      field: 'createdTime',
      headerName: 'Upload Date',
      flex: 1,
      minWidth: 130,
      sortable: false,
      filterable: false,
      disableColumnMenu: true,
      valueFormatter: (value) => formatDateDDMMYYYY(value),
    },
    {
      field: 'progress',
      headerName: 'Status',
      flex: 1,
      minWidth: 200,
      sortable: false,
      filterable: false,
      disableColumnMenu: true,
      align: 'center',
      headerAlign: 'center',
      renderCell: (params) => (
        <PatientProgressCell
          patientId={params.row.id}
          isDemo={isDemo}
          isDemoOffline={isDemoOffline}
        />
      ),
    },
    ...(/** @type {import('@mui/x-data-grid').GridColDef[]} */ (isDemoOffline ? [
      {
        field: 'offlineStatus',
        headerName: 'Access Mode',
        flex: 1,
        minWidth: 140,
        sortable: false,
        filterable: false,
        disableColumnMenu: true,
        align: 'center',
        headerAlign: 'center',
        renderCell: (/** @type {any} */ params) => (
          <OfflineStatusCell patientId={params.row.id} />
        ),
      },
    ] : [])),
    ...(/** @type {import('@mui/x-data-grid').GridColDef[]} */ (isDemoOffline ? [
      {
        field: 'offlineActions',
        headerName: 'Offline Actions',
        flex: 1,
        minWidth: 140,
        sortable: false,
        filterable: false,
        disableColumnMenu: true,
        align: 'center',
        headerAlign: 'center',
        renderCell: (/** @type {any} */ params) => (
          <OfflineActionsCell
            patientId={params.row.id}
            displayName={params.row.name}
            createdTime={params.row.createdTime ?? 0}
          />
        ),
      },
    ] : [])),
  ], [isDemo, isDemoOffline]);

  return (
    <div
      style={{
        width: '75%',
        maxWidth: 1200,
        margin: '0 auto',
        paddingTop: '5vw',
        paddingBottom: '2vw',
      }}
    >
      <div style={{
        border: '1px solid #e0e0e0',
        borderRadius: 8,
        background: '#fff',
        overflow: 'hidden',
      }}>
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          paddingTop: 25,
          paddingBottom: 8,
          paddingLeft: 7,
          paddingRight: 24,
          borderBottom: 'none',
          flexWrap: 'wrap',
          gap: 12,
        }}>
          <div style={{ fontWeight: 400, fontSize: 32 }}>
            Patients
          </div>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <TextField
              label="Search patients"
              placeholder="Search by name…"
              size="small"
              value={searchTerm}
              onChange={handleSearchChange}
              error={isSearching && error}
              sx={{ minWidth: 220 }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" />
                  </InputAdornment>
                ),
              }}
              inputProps={{
                'aria-describedby': isSearching && error ? SEARCH_ERROR_ALERT_ID : undefined,
              }}
            />
            { !isDemo && <UploadButton storage={storage} /> }
          </Box>
        </div>

        {isSearching && searchTruncated && !loading && !error ? (
          <Alert severity="info" sx={{ mx: 1, mb: 1 }}>
            Showing the first {rows.length} matches — refine your search for more precise results.
          </Alert>
        ) : null}

        {error ? (
          <Alert severity="error" id={isSearching ? SEARCH_ERROR_ALERT_ID : undefined} sx={{ mx: 1, mb: 1 }}>
            {isSearching ? 'Search failed. Please try again.' : 'Failed to load patients. Please try again.'}
          </Alert>
        ) : null}

        {isSearching && !loading && !error && rows.length === 0 ? (
          <Typography sx={{ px: 2, py: 4, textAlign: 'center' }} color="text.secondary">
            No patients match &quot;{searchTerm}&quot;.
          </Typography>
        ) : (
          <DataGrid
            rows={error ? [] : rows}
            loading={loading}
            columns={columns}
            paginationMode={paginationMode}
            rowCount={paginationMode === 'server' ? rowCount : undefined}
            paginationModel={paginationModel}
            onPaginationModelChange={onPaginationModelChange}
            pageSizeOptions={pageSizeOptions}
            slotProps={{ pagination: { disabled: loading } }}
            density="compact"
            rowHeight={85}
            hideFooterSelectedRowCount
            hideFooter={isSearching}
            sx={{
              border: 'none',
              borderRadius: 0,
              width: '100%',
              maxWidth: '100%',
              mt: 0,
              '& .MuiDataGrid-columnHeaders': {
                backgroundColor: 'transparent',
              },
              '& .MuiDataGrid-columnHeader': {
                backgroundColor: 'transparent',
              },
              '& .MuiDataGrid-columnHeaderTitle': {
                fontWeight: 590,
                color: 'common.black',
                fontSize: 17,
              },
              '& .MuiDataGrid-columnHeader:focus, & .MuiDataGrid-columnHeader:focus-within': {
                outline: 'none',
              },
              '& .MuiDataGrid-row': {
                minHeight: 64,
                maxHeight: 64,
              },
            }}
            aria-label="Patient details table"
          />
        )}
      </div>
    </div>
  );
}
